import { useMemo } from 'react'
import type { NotificationDocument } from '@/lib/db/rxdb/schemas/notification'
import { useNotifications as useRxDBNotifications } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'

/**
 * Hook that returns all notifications for the current user
 * Notifications are ordered by creation date (newest first)
 */
export function useNotifications() {
  const userId = useLoginStore((state) => state.userId)

  // Get notifications from RxDB
  const { notifications: allNotifications, isLoading } = useRxDBNotifications({
    userId: userId || undefined,
  })

  // Sort notifications by creation date (newest first)
  const notifications = useMemo(() => {
    if (!userId) {
      return []
    }

    return [...allNotifications]
      .filter((notif: NotificationDocument) => notif.userId === userId)
      .sort(
        (a: NotificationDocument, b: NotificationDocument) =>
          b.createdOn - a.createdOn,
      )
  }, [userId, allNotifications])

  return {
    notifications,
    isLoading,
  }
}

/**
 * Hook that returns only unread notifications
 */
export function useUnreadNotifications() {
  const userId = useLoginStore((state) => state.userId)

  // Get notifications from RxDB
  const { notifications: allNotifications, isLoading } = useRxDBNotifications({
    userId: userId || undefined,
    isRead: false,
  })

  // Sort unread notifications by creation date (newest first)
  const notifications = useMemo(() => {
    if (!userId) {
      return []
    }

    return [...allNotifications]
      .filter(
        (notif: NotificationDocument) =>
          notif.userId === userId && !notif.isRead,
      )
      .sort(
        (a: NotificationDocument, b: NotificationDocument) =>
          b.createdOn - a.createdOn,
      )
  }, [userId, allNotifications])

  return {
    notifications,
    isLoading,
  }
}

/**
 * Hook that returns the count of unread notifications
 * Useful for badge display
 */
export function useUnreadCount() {
  const userId = useLoginStore((state) => state.userId)

  // Get unread notifications from RxDB
  const { notifications: allNotifications, isLoading } = useRxDBNotifications({
    userId: userId || undefined,
    isRead: false,
  })

  const count = useMemo(() => {
    if (!userId) {
      return 0
    }

    return allNotifications.filter(
      (notif: NotificationDocument) => notif.userId === userId && !notif.isRead,
    ).length
  }, [userId, allNotifications])

  return {
    count,
    isLoading,
  }
}
